/**
 * PSSP LABS — "PSSP Precision" theme
 * procedural-3d.js — <pssp-procedural> dependency-free CAD-style wireframe.
 *
 * Draws an animated wireframe placeholder on a plain 2D canvas — no Three.js,
 * no WebGL, no network requests (BUILD-SPEC §5). Used by the interactive 3D
 * hero's "Procedural placeholder" mode.
 *
 * Markup:
 *   <pssp-procedural data-variant="turned|lattice|ring|letter"
 *                    data-line-color="#..." data-accent-color="#...">
 *     (optional slotted fallback markup — left visible if no 2D context)
 *   </pssp-procedural>
 *
 * Behavior:
 * - dpr-aware canvas, resized via ResizeObserver (window resize fallback).
 * - Colors default from the element's computed `color` and `--accent` var.
 * - Perspective projection, Y rotation ~0.25 rad/s + gentle X tilt via rAF.
 * - Line alpha attenuates with depth; every 7th edge uses the accent color.
 * - Faint corner dimension-line garnish with a mono label.
 * - Static single frame when PSSPTheme.motionOK() is false (live-updated).
 * - Pauses offscreen and while the tab is hidden; full cleanup on disconnect.
 */

(function () {
  'use strict';

  if (customElements.get('pssp-procedural')) return;

  var TWO_PI = Math.PI * 2;
  var YAW_SPEED = 0.25; /* rad/s */
  var STATIC_YAW = 0.8; /* nice 3/4 view for the reduced-motion frame */
  var BASE_TILT = 0.45; /* rad */

  function motionOK() {
    if (window.PSSPTheme && typeof window.PSSPTheme.motionOK === 'function') {
      return window.PSSPTheme.motionOK();
    }
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ======================================================================
     Mesh generators — each returns { verts: [x,y,z][], edges: [a,b][] }.
     All meshes are normalized (centered, max half-extent = 1) afterwards.
     ====================================================================== */

  /** Recenter to the bounding-box middle and scale so max |coord| === 1. */
  function normalize(mesh) {
    var min = [Infinity, Infinity, Infinity];
    var max = [-Infinity, -Infinity, -Infinity];
    mesh.verts.forEach(function (v) {
      for (var i = 0; i < 3; i++) {
        if (v[i] < min[i]) min[i] = v[i];
        if (v[i] > max[i]) max[i] = v[i];
      }
    });
    var center = [
      (min[0] + max[0]) / 2,
      (min[1] + max[1]) / 2,
      (min[2] + max[2]) / 2
    ];
    var extent = Math.max(
      max[0] - center[0],
      max[1] - center[1],
      max[2] - center[2],
      1e-6
    );
    mesh.verts = mesh.verts.map(function (v) {
      return [
        (v[0] - center[0]) / extent,
        (v[1] - center[1]) / extent,
        (v[2] - center[2]) / extent
      ];
    });
    return mesh;
  }

  /**
   * "turned" — lathe-revolved machined shaft profile: flange, journal,
   * relief groove, taper, spigot. Profile rows are [y, radius] pairs
   * revolved around the Y axis with 28 segments.
   */
  function meshTurned() {
    var profile = [
      [-1.0, 0.3],
      [-0.82, 0.3],
      [-0.82, 0.46],
      [-0.62, 0.46],
      [-0.62, 0.26],
      [-0.1, 0.26],
      [-0.04, 0.2],
      [0.06, 0.2],
      [0.12, 0.26],
      [0.45, 0.26],
      [0.6, 0.14],
      [0.86, 0.14],
      [0.86, 0.06],
      [1.0, 0.06]
    ];
    var segments = 28;
    var verts = [];
    var edges = [];
    var row;
    var seg;

    for (row = 0; row < profile.length; row++) {
      for (seg = 0; seg < segments; seg++) {
        var angle = (seg / segments) * TWO_PI;
        verts.push([
          Math.cos(angle) * profile[row][1],
          profile[row][0],
          Math.sin(angle) * profile[row][1]
        ]);
      }
    }
    for (row = 0; row < profile.length; row++) {
      for (seg = 0; seg < segments; seg++) {
        var here = row * segments + seg;
        /* Ring edge around the revolve. */
        edges.push([here, row * segments + ((seg + 1) % segments)]);
        /* Longitudinal rail every other segment, joining profile rows. */
        if (row < profile.length - 1 && seg % 2 === 0) {
          edges.push([here, (row + 1) * segments + seg]);
        }
      }
    }
    return normalize({ verts: verts, edges: edges });
  }

  /** "lattice" — 4×4×4 orthogonal strut network (periodic lattice cell). */
  function meshLattice() {
    var n = 4;
    var verts = [];
    var edges = [];
    var index = function (x, y, z) {
      return x * n * n + y * n + z;
    };
    var map = function (i) {
      return (i / (n - 1)) * 1.5 - 0.75;
    };
    var x;
    var y;
    var z;

    for (x = 0; x < n; x++) {
      for (y = 0; y < n; y++) {
        for (z = 0; z < n; z++) {
          verts.push([map(x), map(y), map(z)]);
        }
      }
    }
    for (x = 0; x < n; x++) {
      for (y = 0; y < n; y++) {
        for (z = 0; z < n; z++) {
          if (x < n - 1) edges.push([index(x, y, z), index(x + 1, y, z)]);
          if (y < n - 1) edges.push([index(x, y, z), index(x, y + 1, z)]);
          if (z < n - 1) edges.push([index(x, y, z), index(x, y, z + 1)]);
        }
      }
    }
    return normalize({ verts: verts, edges: edges });
  }

  /**
   * "ring" — standing torus wireframe (band) + four prong spokes rising to
   * a small faceted stone (octahedron) at the top.
   */
  function meshRing() {
    var R = 0.58; /* major radius */
    var r = 0.14; /* tube radius */
    var majorSegments = 30;
    var minorSegments = 8;
    var verts = [];
    var edges = [];
    var a;
    var b;

    /* Torus with its major circle in the XY plane (ring stands upright). */
    for (a = 0; a < majorSegments; a++) {
      var ma = (a / majorSegments) * TWO_PI;
      for (b = 0; b < minorSegments; b++) {
        var mb = (b / minorSegments) * TWO_PI;
        var radial = R + Math.cos(mb) * r;
        verts.push([
          Math.cos(ma) * radial,
          Math.sin(ma) * radial,
          Math.sin(mb) * r
        ]);
      }
    }
    for (a = 0; a < majorSegments; a++) {
      for (b = 0; b < minorSegments; b++) {
        var here = a * minorSegments + b;
        /* Along the tube cross-section. */
        edges.push([here, a * minorSegments + ((b + 1) % minorSegments)]);
        /* Along the band. */
        edges.push([here, ((a + 1) % majorSegments) * minorSegments + b]);
      }
    }

    /* Faceted stone: small octahedron centered above the band. */
    var gemY = R + r + 0.24;
    var gemR = 0.13;
    var gemBase = verts.length;
    verts.push([0, gemY + gemR, 0]); /* apex */
    verts.push([0, gemY - gemR, 0]); /* culet */
    verts.push([gemR, gemY, 0]);
    verts.push([0, gemY, gemR]);
    verts.push([-gemR, gemY, 0]);
    verts.push([0, gemY, -gemR]);
    var e;
    for (e = 0; e < 4; e++) {
      edges.push([gemBase, gemBase + 2 + e]); /* apex → girdle */
      edges.push([gemBase + 1, gemBase + 2 + e]); /* culet → girdle */
      edges.push([gemBase + 2 + e, gemBase + 2 + ((e + 1) % 4)]); /* girdle */
    }

    /* Four prong spokes from the band top to the stone girdle. */
    var prongBase = verts.length;
    verts.push([0.16, R + r * 0.4, 0.1]);
    verts.push([0.16, R + r * 0.4, -0.1]);
    verts.push([-0.16, R + r * 0.4, 0.1]);
    verts.push([-0.16, R + r * 0.4, -0.1]);
    edges.push([prongBase, gemBase + 2]);
    edges.push([prongBase + 1, gemBase + 5]);
    edges.push([prongBase + 2, gemBase + 3]);
    edges.push([prongBase + 3, gemBase + 4]);

    return normalize({ verts: verts, edges: edges });
  }

  /** "letter" — extruded block "P" outline (channel-letter form). */
  function meshLetter() {
    var depth = 0.22;
    /* Outer contour + inner counter of a blocky "P" (x right, y up). */
    var contours = [
      [
        [-0.45, -0.9],
        [-0.45, 0.9],
        [0.3, 0.9],
        [0.5, 0.75],
        [0.5, 0.15],
        [0.3, 0.0],
        [-0.13, 0.0],
        [-0.13, -0.9]
      ],
      [
        [-0.13, 0.3],
        [-0.13, 0.6],
        [0.18, 0.6],
        [0.26, 0.5],
        [0.26, 0.4],
        [0.18, 0.3]
      ]
    ];
    var verts = [];
    var edges = [];

    contours.forEach(function (contour) {
      var count = contour.length;
      var base = verts.length;
      var i;
      /* Front face loop (z = +depth), then back face loop (z = -depth). */
      for (i = 0; i < count; i++) {
        verts.push([contour[i][0], contour[i][1], depth]);
      }
      for (i = 0; i < count; i++) {
        verts.push([contour[i][0], contour[i][1], -depth]);
      }
      for (i = 0; i < count; i++) {
        var next = (i + 1) % count;
        edges.push([base + i, base + next]); /* front outline */
        edges.push([base + count + i, base + count + next]); /* back outline */
        edges.push([base + i, base + count + i]); /* extrusion rail */
      }
    });

    return normalize({ verts: verts, edges: edges });
  }

  var GENERATORS = {
    turned: meshTurned,
    lattice: meshLattice,
    ring: meshRing,
    letter: meshLetter
  };

  /* Decorative sample labels for the dimension-line garnish (unit-normalized
     values — deliberately not real product dimensions or tolerances). */
  var LABELS = {
    turned: 'Ø 1.000',
    lattice: '4 × 4 × 4',
    ring: 'Ø 1.000',
    letter: 'H 1.000'
  };

  customElements.define(
    'pssp-procedural',
    class extends HTMLElement {
      connectedCallback() {
        this._raf = 0;
        this._running = false;
        this._offscreen = false;
        this._yaw = STATIC_YAW;
        this._time = 0;
        this._last = 0;
        this._w = 0;
        this._h = 0;

        var variant = GENERATORS[this.dataset.variant] ? this.dataset.variant : 'turned';
        this._label = LABELS[variant];
        this._mesh = GENERATORS[variant]();
        /* Scratch buffers reused every frame (no per-frame allocation). */
        this._px = new Float32Array(this._mesh.verts.length);
        this._py = new Float32Array(this._mesh.verts.length);
        this._pz = new Float32Array(this._mesh.verts.length);

        /* Reuse the canvas if the element was moved in the theme editor. */
        this._canvas = this.querySelector('canvas.procedural-3d__canvas');
        if (!this._canvas) {
          this._canvas = document.createElement('canvas');
          this._canvas.className = 'procedural-3d__canvas';
          this._canvas.setAttribute('aria-hidden', 'true');
          this._canvas.style.display = 'block';
          this._canvas.style.width = '100%';
          this._canvas.style.height = '100%';
        }

        this._ctx = this._canvas.getContext('2d');
        if (!this._ctx) {
          /* No 2D context: keep any slotted fallback visible, do nothing. */
          if (this._canvas.parentNode === this) this._canvas.remove();
          this._canvas = null;
          return;
        }

        /* Canvas works: hide slotted fallback content. */
        var child = this.firstElementChild;
        while (child) {
          if (child !== this._canvas) child.setAttribute('hidden', '');
          child = child.nextElementSibling;
        }
        if (this._canvas.parentNode !== this) this.appendChild(this._canvas);

        this._resolveColors();

        this._onTick = this._tick.bind(this);

        /* dpr-aware sizing. */
        this._onResize = this._resize.bind(this);
        if (typeof ResizeObserver !== 'undefined') {
          this._ro = new ResizeObserver(this._onResize);
          this._ro.observe(this);
        } else {
          window.addEventListener('resize', this._onResize);
        }

        /* Pause offscreen. */
        this._io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this._offscreen = !entry.isIntersecting;
              this._syncRunning();
            });
          },
          { rootMargin: '50px' }
        );
        this._io.observe(this);

        /* Pause in hidden tabs. */
        this._onVisibility = () => this._syncRunning();
        document.addEventListener('visibilitychange', this._onVisibility);

        /* Live reduced-motion switch: stop the loop / render a still. */
        this._mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        this._onMqChange = () => {
          this._resolveColors();
          this._syncRunning();
          if (!motionOK()) this._render();
        };
        if (typeof this._mq.addEventListener === 'function') {
          this._mq.addEventListener('change', this._onMqChange);
        }

        this._resize();
        this._syncRunning();
      }

      disconnectedCallback() {
        this._stop();
        if (this._ro) this._ro.disconnect();
        if (this._io) this._io.disconnect();
        if (this._onResize && !this._ro) {
          window.removeEventListener('resize', this._onResize);
        }
        if (this._onVisibility) {
          document.removeEventListener('visibilitychange', this._onVisibility);
        }
        if (this._mq && this._onMqChange && typeof this._mq.removeEventListener === 'function') {
          this._mq.removeEventListener('change', this._onMqChange);
        }
        this._ro = null;
        this._io = null;
        this._onResize = null;
        this._onVisibility = null;
        this._onMqChange = null;
        this._ctx = null;
      }

      /* ----- colors / sizing ----- */

      _resolveColors() {
        var style = window.getComputedStyle(this);
        this._lineColor =
          (this.dataset.lineColor || style.color || '#e6e9ec').trim();
        var accentVar = style.getPropertyValue('--accent').trim();
        this._accentColor =
          (this.dataset.accentColor || accentVar || this._lineColor).trim();
      }

      _resize() {
        if (!this._canvas || !this._ctx) return;
        var w = this.clientWidth;
        var h = this.clientHeight;
        if (w < 2 || h < 2) return;
        var dpr = Math.min(window.devicePixelRatio || 1, 2);
        this._w = w;
        this._h = h;
        this._canvas.width = Math.round(w * dpr);
        this._canvas.height = Math.round(h * dpr);
        this._ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        /* Always repaint after a resize — covers the static-frame case. */
        if (!this._running) this._render();
      }

      /* ----- run state ----- */

      _syncRunning() {
        var shouldRun = motionOK() && !this._offscreen && !document.hidden;
        if (shouldRun && !this._running) {
          this._start();
        } else if (!shouldRun && this._running) {
          this._stop();
        }
      }

      _start() {
        this._running = true;
        this._last = 0;
        this._raf = window.requestAnimationFrame(this._onTick);
      }

      _stop() {
        this._running = false;
        if (this._raf) window.cancelAnimationFrame(this._raf);
        this._raf = 0;
      }

      _tick(timestamp) {
        if (!this._running || !this.isConnected) return;
        this._raf = window.requestAnimationFrame(this._onTick);
        if (this._last === 0) {
          this._last = timestamp;
          return;
        }
        var dt = Math.min((timestamp - this._last) / 1000, 0.05);
        this._last = timestamp;
        this._yaw = (this._yaw + YAW_SPEED * dt) % TWO_PI;
        this._time += dt;
        this._render();
      }

      /* ----- rendering ----- */

      _render() {
        var ctx = this._ctx;
        if (!ctx || this._w < 2 || this._h < 2) return;

        var w = this._w;
        var h = this._h;
        ctx.clearRect(0, 0, w, h);

        var cx = w / 2;
        var cy = h / 2;
        var focal = Math.min(w, h) * 1.05;
        var distance = 3.1;
        var tilt = BASE_TILT + (this._running ? Math.sin(this._time * 0.4) * 0.05 : 0);

        var sinY = Math.sin(this._yaw);
        var cosY = Math.cos(this._yaw);
        var sinX = Math.sin(tilt);
        var cosX = Math.cos(tilt);

        var verts = this._mesh.verts;
        var edges = this._mesh.edges;
        var i;

        /* Project all vertices: rotate Y, tilt X, perspective divide. */
        for (i = 0; i < verts.length; i++) {
          var v = verts[i];
          var x1 = v[0] * cosY + v[2] * sinY;
          var z1 = -v[0] * sinY + v[2] * cosY;
          var y1 = v[1] * cosX - z1 * sinX;
          var z2 = v[1] * sinX + z1 * cosX;
          var scale = focal / (distance + z2);
          this._px[i] = cx + x1 * scale;
          this._py[i] = cy - y1 * scale;
          this._pz[i] = z2;
        }

        ctx.lineWidth = 1;
        ctx.lineCap = 'round';

        for (i = 0; i < edges.length; i++) {
          var a = edges[i][0];
          var b = edges[i][1];
          var depth = (this._pz[a] + this._pz[b]) / 2;
          /* Nearer edges (smaller z) draw more opaque. */
          var alpha = 0.62 - depth * 0.35;
          if (alpha < 0.06) alpha = 0.06;
          if (alpha > 0.92) alpha = 0.92;
          var isAccent = i % 7 === 0;
          ctx.globalAlpha = isAccent ? Math.min(alpha + 0.1, 1) : alpha;
          ctx.strokeStyle = isAccent ? this._accentColor : this._lineColor;
          ctx.beginPath();
          ctx.moveTo(this._px[a], this._py[a]);
          ctx.lineTo(this._px[b], this._py[b]);
          ctx.stroke();
        }

        ctx.globalAlpha = 1;
        this._drawGarnish(ctx, w, h);
      }

      /** Faint CAD garnish: dimension line + ticks + mono label + crosshair. */
      _drawGarnish(ctx, w, h) {
        var pad = Math.max(14, Math.min(w, h) * 0.06);
        ctx.save();
        ctx.globalAlpha = 0.4;
        ctx.strokeStyle = this._lineColor;
        ctx.fillStyle = this._lineColor;
        ctx.lineWidth = 1;

        /* Bottom dimension line with end ticks. */
        var y = h - pad;
        ctx.beginPath();
        ctx.moveTo(pad, y);
        ctx.lineTo(w - pad, y);
        ctx.moveTo(pad, y - 5);
        ctx.lineTo(pad, y + 5);
        ctx.moveTo(w - pad, y - 5);
        ctx.lineTo(w - pad, y + 5);
        ctx.stroke();

        /* Mono label centered above the line. */
        ctx.font = '10px ui-monospace, "SF Mono", Consolas, Menlo, monospace';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(this._label, w / 2, y - 4);

        /* Top-left crosshair mark. */
        ctx.beginPath();
        ctx.moveTo(pad - 6, pad);
        ctx.lineTo(pad + 6, pad);
        ctx.moveTo(pad, pad - 6);
        ctx.lineTo(pad, pad + 6);
        ctx.stroke();

        ctx.restore();
      }
    }
  );
})();
